import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { TitleCaseH1, TitleCaseP } from '@/components/TitleCase';
import { ChevronRight, TrendingUp, Shield, Users, BookOpen, Calculator, ArrowRight, CheckCircle, Download, Play, Building2, Home, Rocket, Banknote, FileText, PieChart } from 'lucide-react';

const AlternativeInvestmentsGuide = () => {
  const [activeTab, setActiveTab] = useState('basics');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const investmentTypes = [
    {
      title: 'JSE Listed Structured Notes',
      description: '100% capital protected with guaranteed returns and S&P 500 upside',
      returns: '18.15% (Y1) + 36.30% (Y3)',
      risk: 'Low',
      minInvestment: 'R250,000',
      icon: FileText
    },
    {
      title: 'Linked Endowments',
      description: 'Tax-efficient 5-year investments with international equity exposure',
      returns: '33.54% (Y3) + Index Growth',
      risk: 'Low-Medium',
      minInvestment: 'R100,000',
      icon: PieChart
    },
    {
      title: 'Real Estate Funds',
      description: 'Industrial property investments with stable income streams',
      returns: '12-15% annually',
      risk: 'Medium',
      minInvestment: 'R100,000',
      icon: Building2
    },
    {
      title: 'Zero Coupon Bonds',
      description: 'Capital guaranteed bonds from top SA banks',
      returns: '8-12% annually',
      risk: 'Low',
      minInvestment: 'R50,000',
      icon: Banknote
    }
  ];

  const benefits = [
    { icon: TrendingUp, title: 'Higher Returns', description: 'Potential for 10-25% annual returns vs 6-8% traditional markets' },
    { icon: Shield, title: 'Diversification', description: 'Reduce portfolio risk through uncorrelated asset classes' },
    { icon: Users, title: 'Exclusive Access', description: 'Access to investments typically reserved for institutions' },
    { icon: BookOpen, title: 'Inflation Hedge', description: 'Protection against inflation through real asset exposure' }
  ];

  const faqs = [
    {
      question: 'What exactly are alternative investments?',
      answer: 'Alternative investments are asset classes beyond traditional stocks, bonds, and cash. They include private equity, real estate, venture capital, private credit, commodities, and hedge funds. These investments often have different risk-return profiles and can provide diversification benefits.'
    },
    {
      question: 'How much should I allocate to alternatives?',
      answer: 'Financial experts typically recommend 10-30% of your portfolio in alternatives, depending on your risk tolerance, investment timeline, and financial goals. Start with 5-10% if you\'re new to alternatives and gradually increase as you become more comfortable.'
    },
    {
      question: 'Are alternative investments only for wealthy investors?',
      answer: 'Not anymore! While alternatives were traditionally exclusive to high-net-worth individuals, platforms like ALTA-X have democratized access. You can start with as little as R25,000 in many alternative investment opportunities.'
    },
    {
      question: 'What are the main risks I should know about?',
      answer: 'Key risks include liquidity constraints (longer lock-up periods), higher fees, complexity, and potentially higher volatility. However, these risks are often offset by higher return potential and diversification benefits when properly managed.'
    },
    {
      question: 'How do I get started with alternative investments?',
      answer: 'Start by educating yourself (you\'re already doing that!), assess your risk tolerance, and consider starting with a small allocation. ALTA-X provides access to vetted alternative investments with lower minimums and professional management.'
    }
  ];



  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[hsl(var(--navy))] via-[hsl(var(--navy-dark))] to-[hsl(var(--navy-light))] text-white pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center bg-[hsl(var(--navy-dark))]/30 rounded-full px-4 py-2 mb-6">
              <BookOpen className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">Free Investment Education</span>
            </div>
            <TitleCaseH1 className="text-4xl md:text-6xl font-bold mb-6 font-serif tracking-tight">
              Master Alternative Investments
            </TitleCaseH1>
            <TitleCaseP className="text-xl text-white/80 max-w-3xl mx-auto mb-8">
              Learn how to build wealth beyond traditional stocks and bonds. Discover investment strategies used by the world's most successful investors.
            </TitleCaseP>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-gradient-to-r from-[hsl(var(--bronze-gold))] to-[hsl(var(--bronze-gold-light))] hover:from-[hsl(var(--bronze-gold-light))] hover:to-[hsl(var(--bronze-gold))] text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg">
                Start Learning Now
              </button>
              <button className="border-2 border-white/30 text-white/80 hover:bg-white/10 px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center">
                <Play className="w-5 h-5 mr-2" />
                Watch Video Guide
              </button>
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
            <div className="text-center">
              <div className="text-3xl font-bold text-[hsl(var(--bronze-gold))]">100%</div>
              <div className="text-white/70">Capital Protected</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[hsl(var(--bronze-gold))]">R50,000</div>
              <div className="text-white/70">Minimum Investment</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[hsl(var(--bronze-gold))]">5</div>
              <div className="text-white/70">Investment Types</div>
            </div>
          </div>
        </div>
      </div>

      {/* What Are Alternative Investments Section */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[hsl(var(--navy))] mb-6">
              What Are Alternative Investments?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Alternative investments are financial assets that don't fall into conventional investment categories like stocks, bonds, or cash.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-8">
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <h3 className="text-2xl font-bold text-[hsl(var(--navy))] mb-6">The Simple Definition</h3>
                <div className="space-y-4 text-gray-700">
                  <p className="text-lg leading-relaxed">
                    <strong>Alternative investments</strong> are any investment that is not a traditional stock, bond, or cash equivalent. They include:
                  </p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-[hsl(var(--bronze-gold))] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span>Private equity and venture capital</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-[hsl(var(--bronze-gold))] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span>Real estate and REITs</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-[hsl(var(--bronze-gold))] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span>Hedge funds and private credit</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-[hsl(var(--bronze-gold))] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span>Commodities and precious metals</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-2 h-2 bg-[hsl(var(--bronze-gold))] rounded-full mt-2 mr-3 flex-shrink-0"></div>
                      <span>Structured products and derivatives</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <h3 className="text-2xl font-bold text-[hsl(var(--navy))] mb-6">Key Characteristics</h3>
                <div className="space-y-4">
                  <div className="border-l-4 border-[hsl(var(--bronze-gold))] pl-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Lower Liquidity</h4>
                    <p className="text-gray-600">Unlike stocks that can be sold instantly, alternatives often have lock-up periods or limited trading windows.</p>
                  </div>
                  <div className="border-l-4 border-[hsl(var(--bronze-gold))] pl-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Higher Minimum Investments</h4>
                    <p className="text-gray-600">Typically require larger initial investments compared to traditional assets.</p>
                  </div>
                  <div className="border-l-4 border-[hsl(var(--bronze-gold))] pl-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Different Risk-Return Profiles</h4>
                    <p className="text-gray-600">Often offer potential for higher returns but with different risk characteristics.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <h3 className="text-2xl font-bold text-[hsl(var(--navy))] mb-6">Traditional vs Alternative</h3>
                <div className="space-y-4">
                  <div className="p-4 bg-red-50 rounded-lg">
                    <h4 className="font-semibold text-red-800 mb-2">Traditional Investments</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>JSE Stocks</span>
                        <span className="font-semibold">6-10% annually</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Government Bonds</span>
                        <span className="font-semibold">4-7% annually</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Money Market</span>
                        <span className="font-semibold">3-5% annually</span>
                      </div>
                    </div>
                    <div className="mt-3 text-xs text-red-700">
                      ✓ High liquidity • ✓ Low minimums • ✗ Limited returns
                    </div>
                  </div>
                  
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h4 className="font-semibold text-green-800 mb-2">Alternative Investments</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Structured Notes</span>
                        <span className="font-semibold">8-20% annually</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Private Equity</span>
                        <span className="font-semibold">12-25% annually</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Real Estate</span>
                        <span className="font-semibold">10-18% annually</span>
                      </div>
                    </div>
                    <div className="mt-3 text-xs text-green-700">
                      ✓ Higher returns • ✓ Diversification • ✗ Lower liquidity
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <h3 className="text-2xl font-bold text-[hsl(var(--navy))] mb-6">Why Smart Investors Choose Alternatives</h3>
                <div className="space-y-4">
                  {benefits.map((benefit, index) => {
                    const IconComponent = benefit.icon;
                    return (
                      <div key={index} className="flex items-start space-x-4">
                        <div className="bg-[hsl(var(--navy))]/10 p-3 rounded-lg">
                          <IconComponent className="w-6 h-6 text-[hsl(var(--navy))]" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">{benefit.title}</h4>
                          <p className="text-gray-600">{benefit.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Deep Dive Education Section */}
      <div className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[hsl(var(--navy))] mb-6">
              Understanding the Fundamentals
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Let's break down the key concepts you need to understand before investing in alternatives.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="bg-white border border-gray-200 rounded-2xl p-8">
              <div className="bg-[hsl(var(--navy))]/10 p-4 rounded-lg w-16 h-16 mb-6 flex items-center justify-center">
                <BookOpen className="w-8 h-8 text-[hsl(var(--navy))]" />
              </div>
              <h3 className="text-2xl font-bold text-[hsl(var(--navy))] mb-4">Correlation</h3>
              <p className="text-gray-600 mb-4">
                Traditional assets (stocks and bonds) often move together during market events. Alternative investments typically have <strong>low correlation</strong> to traditional markets.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Example:</strong> When the JSE drops 20%, your alternative investments might only drop 5% or even gain value, providing portfolio protection.
                </p>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8">
              <div className="bg-[hsl(var(--navy))]/10 p-4 rounded-lg w-16 h-16 mb-6 flex items-center justify-center">
                <Shield className="w-8 h-8 text-[hsl(var(--navy))]" />
              </div>
              <h3 className="text-2xl font-bold text-[hsl(var(--navy))] mb-4">Risk Management</h3>
              <p className="text-gray-600 mb-4">
                Alternatives don't eliminate risk—they change it. You trade <strong>market risk</strong> for <strong>liquidity risk</strong>, <strong>complexity risk</strong>, and sometimes <strong>concentration risk</strong>.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Key Point:</strong> Never invest more than 10-30% of your portfolio in alternatives until you fully understand them.
                </p>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8">
              <div className="bg-[hsl(var(--navy))]/10 p-4 rounded-lg w-16 h-16 mb-6 flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-[hsl(var(--navy))]" />
              </div>
              <h3 className="text-2xl font-bold text-[hsl(var(--navy))] mb-4">Return Expectations</h3>
              <p className="text-gray-600 mb-4">
                Higher returns come with trade-offs. Alternative investments often require <strong>longer time horizons</strong> and <strong>higher minimum investments</strong> to access their return potential.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Reality Check:</strong> A 15% annual return sounds great, but if you can't access your money for 5 years, make sure you can afford to wait.
                </p>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8">
              <div className="bg-[hsl(var(--navy))]/10 p-4 rounded-lg w-16 h-16 mb-6 flex items-center justify-center">
                <Users className="w-8 h-8 text-[hsl(var(--navy))]" />
              </div>
              <h3 className="text-2xl font-bold text-[hsl(var(--navy))] mb-4">Due Diligence</h3>
              <p className="text-gray-600 mb-4">
                Alternative investments are more complex and less regulated than traditional assets. <strong>Research thoroughly</strong> and understand exactly what you're buying.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Essential Questions:</strong> Who manages it? What are the fees? How do I exit? What are the risks?
                </p>
              </div>
            </div>
          </div>

          {/* Portfolio Allocation Guide */}
          <div className="bg-gradient-to-r from-[hsl(var(--navy))]/5 to-[hsl(var(--bronze-gold))]/5 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-[hsl(var(--navy))] mb-6 text-center">How Much Should You Allocate?</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="text-3xl font-bold text-[hsl(var(--bronze-gold))] mb-2">5-10%</div>
                  <div className="font-semibold text-[hsl(var(--navy))] mb-2">Conservative</div>
                  <p className="text-sm text-gray-600">New to alternatives, risk-averse, or nearing retirement</p>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-white rounded-lg p-6 shadow-sm border-2 border-[hsl(var(--bronze-gold))]">
                  <div className="text-3xl font-bold text-[hsl(var(--bronze-gold))] mb-2">10-20%</div>
                  <div className="font-semibold text-[hsl(var(--navy))] mb-2">Moderate</div>
                  <p className="text-sm text-gray-600">Experienced investor with medium risk tolerance</p>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="text-3xl font-bold text-[hsl(var(--bronze-gold))] mb-2">20-30%</div>
                  <div className="font-semibold text-[hsl(var(--navy))] mb-2">Aggressive</div>
                  <p className="text-sm text-gray-600">High net worth, long time horizon, sophisticated investor</p>
                </div>
              </div>
            </div>
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                <strong>Important:</strong> These are general guidelines. Your allocation should depend on your specific financial situation, goals, and risk tolerance.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Investment Types Section */}
      <div className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[hsl(var(--navy))] mb-6">
              Types of Alternative Investments
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore different asset classes and find the ones that match your investment goals and risk tolerance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {investmentTypes.map((type, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 hover:border-blue-300">
                <div className="flex items-center mb-6">
                  <div className="bg-[hsl(var(--navy))]/10 p-3 rounded-lg mr-4">
                    <type.icon className="w-8 h-8 text-[hsl(var(--navy))]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[hsl(var(--navy))]">{type.title}</h3>
                </div>
                <p className="text-gray-600 mb-6">{type.description}</p>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div>
                    <div className="text-sm text-gray-500">Returns</div>
                    <div className="font-semibold text-[hsl(var(--bronze-gold))]">{type.returns}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Risk Level</div>
                    <div className="font-semibold text-[hsl(var(--navy))]">{type.risk}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Min. Investment</div>
                    <div className="font-semibold text-[hsl(var(--navy))]">{type.minInvestment}</div>
                  </div>
                </div>
                <button className="w-full bg-gradient-to-r from-[hsl(var(--navy))] to-[hsl(var(--navy-dark))] text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300">
                  Learn More
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Key Features */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[hsl(var(--navy))] mb-6">
              Why Choose ALTA-X Alternative Investments?
            </h2>
            <p className="text-xl text-gray-600">
              Professional-grade investments with institutional backing
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
              <div className="bg-[hsl(var(--navy))]/10 p-4 rounded-full w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <Shield className="w-8 h-8 text-[hsl(var(--navy))]" />
              </div>
              <h3 className="text-xl font-bold text-[hsl(var(--navy))] mb-4">FSCA Regulated</h3>
              <p className="text-gray-600">All investments are regulated by the Financial Sector Conduct Authority, ensuring compliance and investor protection.</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
              <div className="bg-[hsl(var(--navy))]/10 p-4 rounded-full w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <Building2 className="w-8 h-8 text-[hsl(var(--navy))]" />
              </div>
              <h3 className="text-xl font-bold text-[hsl(var(--navy))] mb-4">Top Bank Backing</h3>
              <p className="text-gray-600">Our structured products are backed by South Africa's top 4 banks, providing institutional-grade security.</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
              <div className="bg-[hsl(var(--navy))]/10 p-4 rounded-full w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-[hsl(var(--navy))]" />
              </div>
              <h3 className="text-xl font-bold text-[hsl(var(--navy))] mb-4">Proven Performance</h3>
              <p className="text-gray-600">Track record of delivering consistent returns through various market conditions with capital protection.</p>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[hsl(var(--navy))] mb-6">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Get answers to the most common questions about alternative investments
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg">
                <button
                  className="w-full px-6 py-4 text-left font-semibold text-[hsl(var(--navy))] hover:bg-gray-50 flex justify-between items-center"
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                >
                  {faq.question}
                  <ChevronRight className={`w-5 h-5 transition-transform ${expandedFaq === index ? 'rotate-90' : ''}`} />
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-4 text-gray-600">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-[hsl(var(--navy))] to-[hsl(var(--navy-dark))] text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Start Your Alternative Investment Journey?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Join thousands of investors who have discovered the power of alternative investments. Start with as little as R25,000.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link 
              to="/investments" 
              className="bg-gradient-to-r from-[hsl(var(--bronze-gold))] to-[hsl(var(--bronze-gold-light))] hover:from-[hsl(var(--bronze-gold-light))] hover:to-[hsl(var(--bronze-gold))] text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg inline-flex items-center justify-center"
            >
              Explore Investments
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <button className="border-2 border-white/30 text-white/80 hover:bg-white/10 px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center">
              <Download className="w-5 h-5 mr-2" />
              Download Free Guide
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-[hsl(var(--bronze-gold))] mr-2" />
              <span>FSCA Regulated</span>
            </div>
            <div className="flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-[hsl(var(--bronze-gold))] mr-2" />
              <span>No Hidden Fees</span>
            </div>
            <div className="flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-[hsl(var(--bronze-gold))] mr-2" />
              <span>Expert Support</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlternativeInvestmentsGuide;
